import { ArcgisEndpointConstant } from '@gishub/domain';
import type { LayerStatisticEntity, StatisticDefinition } from '@gishub/domain';
import { StatisticTypeEnum } from '@gishub/domain';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { ArcGisQueryRepositoryProvider } from '../../../../shared/providers/repository-providers';
import { UbigeoProvider } from '../../shared/layer/ubigeo/ubigeo-provider';
import { FireKpiState } from './fire-kpi-state';
import { expressionFor, resolveFireScope } from './fire-scope';

/** Campo de SERFOR por el que se agrupa el estado del incendio. */
const STATE_FIELD = 'ESTADO';
/** Campo neutro para el conteo (necesario aunque `COUNT` no lo use). */
const COUNT_FIELD = 'OBJECTID';

const INITIAL_STATE: FireKpiState = {
  loading: true,
  error: false,
  scopeCode: null,
  scopeLabel: 'Nacional',
  alertado: 0,
  confirmado: 0,
  controlado: 0,
  extinguido: 0,
  total: 0,
  active: 0,
  computedAt: null,
};

/**
 * FireKpiNotifier — conteo de incendios forestales (SERFOR) del header.
 *
 * Sigue el patrón de la consola: un notifier reactivo que escucha el ubigeo
 * del header y recarga solo cuando el ÁMBITO cambia de verdad (cargar la
 * lista de provincias no debe disparar una consulta nueva). Una sola petición
 * `outStatistics` agrupada por `ESTADO` entrega total + desglose, así el
 * header no espera N consultas ni descarga miles de registros solo para
 * contarlos.
 *
 * Regla compartida con el resto de KPI: si el servicio falla se marca
 * `error` y el componente muestra "sin dato"; un 0 sin contexto confundiría
 * más que ayudar.
 */
export class FireKpiNotifier extends BaseNotifier<FireKpiState> {
  /**
   * Token de la consulta en vuelo. Cada ámbito nuevo lo incrementa: la
   * respuesta de una selección anterior que llegue tarde se descarta y no
   * pisa el conteo vigente.
   */
  private _seq = 0;

  constructor(ref: Ref) {
    super(ref, INITIAL_STATE);
  }

  private get ubigeo(): UbigeoProvider {
    return this.ref.watch(UbigeoProvider);
  }

  private get queryRepo() {
    return this.ref.read(ArcGisQueryRepositoryProvider).instance;
  }

  override build(): void {
    // Reactividad al ubigeo. Se lee el objeto de estado COMPLETO para que el
    // efecto se re-evalúe ante cualquier cambio; el guard de abajo descarta
    // las que no mueven el ámbito (p. ej. al poblar provincia/distrito).
    this.listen(() => {
      const scope = resolveFireScope(this.ubigeo.state());

      const prev = this.snapshot();
      if (scope.code === prev.scopeCode && prev.computedAt !== null) return;

      this.updateState((state) => ({
        ...state,
        scopeCode: scope.code,
        scopeLabel: scope.label,
      }));
      void this.load();
    });
  }

  /** Consulta el desglose por estado del ámbito y actualiza el estado. */
  private async load(): Promise<void> {
    const token = ++this._seq;
    const scopeCode = this.snapshot().scopeCode;

    this.updateState((s) => ({ ...s, loading: true, error: false }));

    const url = ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.INCENDIO_FORESTAL);
    const stats: StatisticDefinition[] = [
      {
        statisticType: StatisticTypeEnum.Count,
        onStatisticField: COUNT_FIELD,
        outStatisticFieldName: 'n',
      },
    ];
    const res = await this.queryRepo.queryLayerStatistics(
      url,
      // Sin ámbito el `where` debe ser explícito: una cadena vacía no es válida.
      expressionFor(scopeCode) || '1=1',
      stats,
      [STATE_FIELD],
    );

    // El ámbito cambió otra vez mientras volaba: su consulta gana.
    if (token !== this._seq) return;

    if (!res.success) {
      this.updateState((s) => ({ ...s, loading: false, error: true }));
      return;
    }

    const tally = { alertado: 0, confirmado: 0, controlado: 0, extinguido: 0 };
    let other = 0;

    for (const row of res.data ?? []) {
      const n = FireKpiNotifier.countOf(row);
      switch (Number(row[STATE_FIELD])) {
        case 1: tally.alertado += n; break;
        case 2: tally.confirmado += n; break;
        case 3: tally.controlado += n; break;
        case 4: tally.extinguido += n; break;
        default: other += n;
      }
    }

    const total =
      tally.alertado + tally.confirmado + tally.controlado + tally.extinguido + other;

    this.updateState((s) => ({
      ...s,
      loading: false,
      error: false,
      ...tally,
      total,
      // Riesgo vigente: lo extinguido ya no exige decisión.
      active: total - tally.extinguido,
      computedAt: Date.now(),
    }));
  }

  /** El conteo agregado puede venir como número, cadena o null. */
  private static countOf(row: LayerStatisticEntity): number {
    const n = Number(row?.n);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
}
